import { useState } from 'react';
import {
  PASSPORT_SERVICES223,
  activityLine223,
  areasWithEvidence223,
  competencyLabels223,
  passportServiceLabels223,
  servicesInPassport223,
  sourceLabel223,
  type PassportActivity223,
  type PassportCertificate223,
  type PassportService223,
} from '../domain/growthPassport223';
import { AppFrame, Card, Metric, PrimaryButton, SecondaryButton, StatusBanner } from '../components/ui';
import { GuideCharacter } from '../reactions/GuideCharacter';

const demoActivities: PassportActivity223[] = [
  { serviceId: 'mrm', activityType: 'article.read', count: 12 },
  { serviceId: 'mrm', activityType: 'quiz.complete', count: 8 },
  { serviceId: 'mrm', activityType: 'family.talk', count: 4 },
  { serviceId: 'bookquest', activityType: 'book.complete', count: 2 },
  { serviceId: 'nexmap', activityType: 'concept.map', count: 3 },
  { serviceId: 'lidia', activityType: 'assessment.complete', count: 1 },
];

const demoCompetency: Record<string, { total: number; byService: Record<string, number> }> = {
  literacy: { total: 15, byService: { mrm: 12, bookquest: 2, lidia: 1 } },
  economicUnderstanding: { total: 20, byService: { mrm: 20 } },
  conceptConnection: { total: 11, byService: { mrm: 8, nexmap: 3 } },
  questionExpression: { total: 0, byService: {} },
  practiceRecord: { total: 0, byService: {} },
  familyCooperation: { total: 4, byService: { mrm: 4 } },
  sustainedLearning: { total: 2, byService: { bookquest: 2 } },
};

const demoCertificates: PassportCertificate223[] = [
  { certificateId: 'cert_mrm', issuer: 'mrm', title: 'MRM 경제 탐험 인증', criteria: '경제기사 탐험과 퀴즈 10회 이상', designTheme: 'mrm-snow' },
  { certificateId: 'cert_lidia', issuer: 'lidia', title: 'LIDIA 문해력 인증', criteria: '문해력 평가 완료', designTheme: 'lidia' },
  { certificateId: 'cert_nexpa', issuer: 'nexpa', title: 'NEXPA 통합 성장 인증', criteria: '연결된 서비스 4곳에서 활동 증거 30건 이상', designTheme: 'nexpa-integrated' },
];

export function GrowthPassportDemoScreen223() {
  const [sharing, setSharing] = useState(true);
  const [withdrawn, setWithdrawn] = useState<PassportService223[]>([]);

  const visible = PASSPORT_SERVICES223.filter((serviceId) => (
    serviceId === 'mrm' ? true : sharing && !withdrawn.includes(serviceId)
  ));
  const activities = demoActivities.filter((item) => visible.includes(item.serviceId));
  const certificates = demoCertificates.filter((item) => item.issuer === 'nexpa' ? visible.length >= 2 : visible.includes(item.issuer as PassportService223));
  const competency = Object.fromEntries(Object.entries(demoCompetency).map(([area, value]) => [area, {
    ...value,
    total: Object.entries(value.byService).filter(([serviceId]) => visible.includes(serviceId as PassportService223)).reduce((sum, [, count]) => sum + count, 0),
  }]));
  const areas = areasWithEvidence223(competency);

  const toggleService = (serviceId: PassportService223) => setWithdrawn((current) =>
    current.includes(serviceId) ? current.filter((item) => item !== serviceId) : [...current, serviceId]);

  return <AppFrame title="MRM 2.23 성장 패스포트 데모" backTo="/demo" screenClass="passport-screen-2-23">
    <div className="demo-sample-banner">체험용 고정 성장 데이터 · 실제 계정과 완전 분리</div>
    <StatusBanner />

    <section className="passport-hero-2-23">
      <GuideCharacter guide="snows" pose="celebrate" state="guide" alt="성장 기록을 안내하는 스노스" priority/>
      <div>
        <p className="eyebrow">NEXPA Growth Passport</p>
        <h1>이번 달 성장</h1>
        <p>서비스별 점수를 합산하지 않고 활동 증거를 그대로 보여줍니다. 모든 줄에 어느 서비스에서 온 기록인지 표시돼요.</p>
      </div>
    </section>

    <Card>
      <div className="card-heading"><div><span>연결 상태</span><h2>연계 동의와 탈퇴를 직접 바꿔 보세요</h2></div><strong>{visible.length}곳</strong></div>
      <label className="passport-consent-2-23">
        <input type="checkbox" checked={sharing} onChange={() => setSharing(!sharing)}/>
        <span>서비스 간 학습기록 연계 동의</span>
      </label>
      <div className="passport-service-list-2-23">
        {PASSPORT_SERVICES223.map((serviceId) => {
          const shown = visible.includes(serviceId);
          return <button type="button" key={serviceId} className={shown ? 'linked' : ''} disabled={serviceId === 'mrm'}
            onClick={() => toggleService(serviceId)}>
            <b>{passportServiceLabels223[serviceId]}</b>
            <span>{serviceId === 'mrm' ? '내 서비스' : shown ? '연결됨' : withdrawn.includes(serviceId) ? '탈퇴함' : '동의 필요'}</span>
          </button>;
        })}
      </div>
      <p className="passport-note-2-23">
        {sharing ? '연계 동의를 끄면 다른 서비스 기록이 즉시 빠집니다.' : '연계 동의가 꺼져 있어 MRM 기록만 보입니다.'}
        {' '}탈퇴해도 기록은 지워지지 않고 표시에서만 빠집니다.
      </p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>활동 증거</span><h2>합산하지 않습니다</h2></div><strong>{activities.length}줄</strong></div>
      <div className="passport-activity-list-2-23">
        {activities.map((activity) => (
          <div key={`${activity.serviceId}-${activity.activityType}`}>
            <b>{activityLine223(activity)}</b>
            <span className={`passport-source-2-23 source-${activity.serviceId}`}>{sourceLabel223(activity.serviceId)}</span>
          </div>
        ))}
      </div>
      <p className="passport-note-2-23">서비스마다 평가 척도가 달라, 합친 숫자는 의미 없이 의미 있어 보입니다.</p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>성장 영역</span><h2>영역별 증거 수</h2></div><strong>{areas.length}/7</strong></div>
      <div className="passport-area-list-2-23">
        {areas.map((area) => (
          <div key={area}>
            <b>{competencyLabels223[area]}</b>
            <span>{competency[area].total}건</span>
            <small>{Object.entries(competency[area].byService)
              .filter(([serviceId]) => visible.includes(serviceId as PassportService223))
              .map(([serviceId, count]) => `${sourceLabel223(serviceId as PassportService223)} ${count}`).join(' · ')}</small>
          </div>
        ))}
      </div>
      <div className="passport-metric-row-2-23">
        <Metric label="연결 서비스" value={`${servicesInPassport223(activities).length}곳`}/>
        <Metric label="증거 영역" value={`${areas.length}개`}/>
      </div>
    </Card>

    <Card>
      <div className="card-heading"><div><span>인증서</span><h2>발급은 공통, 기준과 디자인은 서비스별</h2></div><strong>{certificates.length}개</strong></div>
      <div className="passport-certificate-list-2-23">
        {certificates.map((certificate) => (
          <div key={certificate.certificateId} className={`theme-${certificate.designTheme}`}>
            <b>{certificate.title}</b>
            <span className="passport-source-2-23">{sourceLabel223(certificate.issuer)}</span>
            <small>{certificate.criteria}</small>
          </div>
        ))}
      </div>
      <p className="passport-note-2-23">통합 인증만 여러 서비스의 증거를 요구합니다. 나머지는 각 서비스가 자기 기준으로 인증합니다.</p>
    </Card>

    <div className="demo-213-actions">
      <SecondaryButton onClick={() => { setSharing(true); setWithdrawn([]); }}>초기화</SecondaryButton>
      <PrimaryButton onClick={() => setSharing(!sharing)}>연계 동의 전환</PrimaryButton>
    </div>
  </AppFrame>;
}
